import { Platform } from 'react-native';
import { Redirect } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useAuth } from '../hooks/useAuth';
import {
  automationRunIdOfTarget,
  getPostAuthDestinationHref,
  readPostAuthTarget,
} from '../lib/auth-handoff';
import {
  clearUaePassPostAuthTarget,
  readUaePassPostAuthTarget,
} from '../lib/uae-pass-session';
import { useWorkspace } from '../providers/useWorkspace';
import { getWorkspaceDefaultHref } from '../lib/workspace-routes';
import { StartupShell } from '../components/common/StartupShell';

export default function Index() {
  const {
    isAuthenticated,
    isOnboardingCompleted,
    isLoading,
    bootstrapCheckFailed,
    needsPasswordBootstrap,
  } = useAuth();
  const { activeWorkspace } = useWorkspace();
  const isFocused = useIsFocused();
  const pendingPostAuthTarget =
    Platform.OS !== 'web' &&
    isAuthenticated &&
    !needsPasswordBootstrap &&
    !bootstrapCheckFailed &&
    !isOnboardingCompleted
      ? readUaePassPostAuthTarget()
      : null;

  // Index is mounted as the stack ANCHOR beneath deep links (unstable_settings
  // anchor: 'index'), so it renders even when another route is active: and a
  // <Redirect> from an unfocused anchor races the active route's own
  // navigation (e.g. auth-complete → jobs-handoff → job gets hijacked to the
  // workspace default). usePathname() can't gate this (it reports '/' for the
  // anchor's own context), so gate on real screen focus instead.
  if (!isFocused) {
    return null;
  }

  if (isLoading) {
    return <StartupShell />;
  }

  if (!isAuthenticated) {
    // A signed-out visitor who opened a Tasks run link (the web layout stored
    // it before this redirect): login carries it as ?next= so the
    // /auth-complete flow lands on that run instead of home.
    const pendingRunTarget =
      Platform.OS === 'web' ? readPostAuthTarget() : 'root';
    if (automationRunIdOfTarget(pendingRunTarget)) {
      return (
        <Redirect
          href={{ pathname: '/(auth)/login', params: { next: pendingRunTarget } } as any}
        />
      );
    }
    const authRoute = Platform.OS === 'web' ? '/(auth)/login' : '/(auth)/onboarding';
    return <Redirect href={authRoute as any} />;
  }

  if (needsPasswordBootstrap) {
    return <Redirect href="/password-bootstrap" />;
  }

  if (bootstrapCheckFailed) {
    return <Redirect href={'/auth-bootstrap-check' as any} />;
  }

  if (!isOnboardingCompleted) {
    if (pendingPostAuthTarget) {
      return (
        <Redirect href={getPostAuthDestinationHref(pendingPostAuthTarget) as any} />
      );
    }

    return <Redirect href="/(onboarding)" />;
  }

  if (Platform.OS !== 'web') {
    clearUaePassPostAuthTarget();
  }

  // A sign-in that started from a public job "Apply" stores the job as the
  // post-auth target (see routeAfterAuth). The auth-state flip during sign-in
  // remounts the root navigator and can strand the user here at '/' with the
  // ?next= param lost: resume the job instead of the workspace default.
  // /jobs-handoff clears the stored copy once the job actually opens.
  // Same for a shared CV opened from the "Open shared CV" email (issue #246):
  // /cv-grant/<id> stores the grant before handing here, and clears the stored
  // copy once the delegated workspace actually opens.
  // Same again for a CV someone was SENT: /cv-handoff/<token> stores the invite
  // whenever the runtime isn't open yet and hands here. Onboarding claims it on
  // ai-input, but the other two reasons the runtime can be closed
  // (needsPasswordBootstrap, bootstrapCheckFailed) both finish by replacing to
  // '/', so without this branch the invite is silently dropped and the
  // recipient lands on the workspace default with no CV and no error.
  const storedPostAuthTarget = readPostAuthTarget();
  if (
    storedPostAuthTarget === 'skills-cv' ||
    storedPostAuthTarget.startsWith('jobs:') ||
    storedPostAuthTarget.startsWith('join:') ||
    storedPostAuthTarget.startsWith('messages:') ||
    storedPostAuthTarget.startsWith('cv-grant:') ||
    storedPostAuthTarget.startsWith('cv-handoff:') ||
    // A partner's "Continue with Tabbio" request that had to wait for sign-up:
    // resume the consent screen instead of the workspace default, or the
    // partner's callback never fires.
    storedPostAuthTarget.startsWith('oauth:') ||
    // Same for `tabbio login`: the terminal is waiting on its loopback
    // listener for the /cli/connect approval.
    storedPostAuthTarget.startsWith('cli-connect:') ||
    storedPostAuthTarget.startsWith('skill:') ||
    storedPostAuthTarget.startsWith('skill-run:') ||
    // A Tasks run link opened while signed out: the board consumes ?runId=
    // and clears the stored copy once the run panel opens.
    storedPostAuthTarget.startsWith('automation-run:')
  ) {
    return (
      <Redirect href={getPostAuthDestinationHref(storedPostAuthTarget) as any} />
    );
  }

  return <Redirect href={getWorkspaceDefaultHref(activeWorkspace) as any} />;
}
